/* ================================================================
   LFJ STATUS FEED — Checkpoint 64, RE-SOURCED in Checkpoint 65
   ----------------------------------------------------------------
   "What I'm currently doing." A small pixel dialogue chip on the LFJ
   page showing the LATEST status (mood emoji + text + "2h ago"); click
   it to scroll a short history. LFJ page only.

   DATA SOURCE — the bot's existing LFJ menu, NOT a separate file.
   The Discord bot may only write videos.json / gallery.json /
   playlist.json / lf-playlist-overrides.json, and its /publish → LFJ
   flow is literally "Thought of the Moment". Each of those posts is a
   videos.json entry with  destination:["lfj"]  and the thought in
   `note` (the shared loader also aliases it to `thought`). This file
   turns those entries into the feed:
     text  = the thought (clipped to 200 chars)
     mood  = a LEADING emoji in the thought, if there is one
             ("😴 editing the recap" -> mood 😴, text "editing the recap").
             The bot's form has no mood field, so this needs no bot change.
     time  = entry.created_at if present, else decoded from the bot's
             key (lfj-<slug>-<base36 Date.now()>) — that works for
             text-only thought posts. A thought attached to a YouTube
             video has the video id as its key (no time) -> no "ago"
             label, and it sorts by file order (the bot appends).
   No LFJ thoughts at all -> the chip simply never appears.

   LIMITS (Theme Settings > "LFJ Status Feed", passed in as data-*):
     data-max          keep only the newest N entries
     data-expire-days  drop entries older than N days (0 = never)
   The bot is append-only (it never deletes), so these limits are
   enforced HERE, at display time — old posts stay in videos.json but
   fall off the feed. Entries with no known time never expire.

   Standalone: doesn't touch lfj-lfj.js's canvas/modal code. Everything
   from the JSON is set with textContent — a thought can never inject HTML.
================================================================ */
(function () {
  'use strict';

  var root = document.getElementById('lfjStatusFeed');
  if (!root) return;

  function safeRun(label, fn) {
    try { return fn(); } catch (e) { try { console.warn('[lfj-status-feed] ' + label + ':', e); } catch (x) {} }
  }

  var MAX   = parseInt(root.getAttribute('data-max'), 10);
  var DAYS  = parseFloat(root.getAttribute('data-expire-days'));
  var TITLE = root.getAttribute('data-title') || 'STATUS';
  if (!(MAX >= 1)) MAX = 10;
  if (!(DAYS >= 0)) DAYS = 30;
  var MAX_TEXT = 200;

  /* ── data ── */
  function clipCodePoints(str, n) { return Array.from(str).slice(0, n).join(''); }

  var LEADING_EMOJI = null;
  try { LEADING_EMOJI = new RegExp('^(\\p{Extended_Pictographic}(?:\\uFE0F|\\u200D\\p{Extended_Pictographic}|[\\u{1F3FB}-\\u{1F3FF}])*)\\s*', 'u'); } catch (e) { LEADING_EMOJI = null; }

  /* bot key -> time. lfj-<slug>-<base36 Date.now()>; sanity-bounded so a
     random slug tail can never be mistaken for a date. */
  function timeFromKey(key, now) {
    var m = String(key || '').match(/-([0-9a-z]{8,9})$/);
    if (!m) return null;
    var t = parseInt(m[1], 36);
    return (t > 1577836800000 && t < now + 86400000) ? t : null;
  }

  function fromVideos(list, now) {
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (v, i) {
      if (!v || typeof v !== 'object') return;
      if (!Array.isArray(v.destination) || v.destination.indexOf('lfj') === -1) return;
      var raw = String(v.note != null ? v.note : (v.thought != null ? v.thought : '')).trim();
      if (!raw) return;
      var created = Date.parse(v.created_at);
      out.push({ text: raw, time: isNaN(created) ? timeFromKey(v.key, now) : created, idx: i });
    });
    return out;
  }

  function normalize(raw, now) {
    var list = Array.isArray(raw) ? raw : [];
    var out = [];
    list.forEach(function (e, i) {
      if (!e || typeof e !== 'object') return;
      var text = String(e.text != null ? e.text : '').trim();
      var mood = clipCodePoints(String(e.mood != null ? e.mood : '').trim(), 8);
      if (!mood && LEADING_EMOJI) {
        var m = text.match(LEADING_EMOJI);
        if (m && text.slice(m[0].length).trim()) { mood = m[1]; text = text.slice(m[0].length).trim(); }
      }
      if (!text) return;
      if (text.length > MAX_TEXT) text = text.slice(0, MAX_TEXT - 1) + '\u2026';
      var t = typeof e.time === 'number' ? e.time : Date.parse(e.time);
      out.push({ text: text, mood: mood, ts: (t == null || isNaN(t)) ? null : t, idx: e.idx != null ? e.idx : i });
    });
    if (DAYS > 0) {
      var cutoff = now - DAYS * 86400000;
      out = out.filter(function (e) { return e.ts === null || e.ts >= cutoff; });
    }
    /* newest first; entries with no time sort last, later-in-file = newer */
    out.sort(function (a, b) {
      if (a.ts === null && b.ts === null) return b.idx - a.idx;
      if (a.ts === null) return 1;
      if (b.ts === null) return -1;
      return b.ts - a.ts || b.idx - a.idx;
    });
    return out.slice(0, MAX);
  }

  function ago(ts, now) {
    if (ts === null) return '';
    var s = Math.max(0, Math.floor((now - ts) / 1000));
    if (s < 60) return 'just now';
    var m = Math.floor(s / 60);  if (m < 60) return m + 'm ago';
    var h = Math.floor(m / 60);  if (h < 24) return h + 'h ago';
    var d = Math.floor(h / 24);  if (d < 30) return d + 'd ago';
    return Math.floor(d / 30) + 'mo ago';
  }

  /* ── render ── */
  var entries = [];
  var chipEl, panelEl, timeEls = [];

  function moodSpan(mood) {
    var s = document.createElement('span');
    s.className = 'lfj-status-mood';
    s.setAttribute('aria-hidden', 'true');
    if (mood) s.textContent = mood; else s.className += ' is-empty';
    return s;
  }

  function build() {
    root.textContent = '';
    timeEls = [];
    var latest = entries[0];
    var multi = entries.length > 1;

    chipEl = document.createElement(multi ? 'button' : 'div');
    chipEl.className = 'lfj-status-chip';
    if (multi) {
      chipEl.type = 'button';
      chipEl.setAttribute('aria-expanded', 'false');
      chipEl.setAttribute('aria-controls', 'lfjStatusPanel');
    }
    var head = document.createElement('span');
    head.className = 'lfj-status-head';
    head.textContent = TITLE;
    var body = document.createElement('span');
    body.className = 'lfj-status-line';
    body.appendChild(moodSpan(latest.mood));
    var txt = document.createElement('span');
    txt.className = 'lfj-status-text';
    txt.textContent = latest.text;
    body.appendChild(txt);
    var when = document.createElement('span');
    when.className = 'lfj-status-when';
    timeEls.push({ el: when, ts: latest.ts });
    body.appendChild(when);
    if (multi) {
      var caret = document.createElement('span');
      caret.className = 'lfj-status-caret';
      caret.setAttribute('aria-hidden', 'true');
      body.appendChild(caret);
    }
    chipEl.appendChild(head);
    chipEl.appendChild(body);
    root.appendChild(chipEl);

    if (multi) {
      panelEl = document.createElement('div');
      panelEl.id = 'lfjStatusPanel';
      panelEl.className = 'lfj-status-panel';
      panelEl.hidden = true;
      panelEl.setAttribute('role', 'region');
      panelEl.setAttribute('aria-label', TITLE + ' history');
      /* history = the OLDER statuses; the latest is already the chip */
      entries.slice(1).forEach(function (e) {
        var row = document.createElement('div');
        row.className = 'lfj-status-row';
        row.appendChild(moodSpan(e.mood));
        var t = document.createElement('span');
        t.className = 'lfj-status-row-text';
        t.textContent = e.text;
        row.appendChild(t);
        var w = document.createElement('span');
        w.className = 'lfj-status-row-when';
        timeEls.push({ el: w, ts: e.ts });
        row.appendChild(w);
        panelEl.appendChild(row);
      });
      root.appendChild(panelEl);
      chipEl.addEventListener('click', function () { setOpen(panelEl.hidden); });
    }
    updateTimes();
    root.hidden = false;
  }

  function setOpen(open) {
    if (!panelEl) return;
    panelEl.hidden = !open;
    chipEl.setAttribute('aria-expanded', open ? 'true' : 'false');
    root.classList.toggle('is-open', open);
  }

  function updateTimes() {
    var now = Date.now();
    timeEls.forEach(function (t) { t.el.textContent = ago(t.ts, now); });
  }

  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') setOpen(false); });
  document.addEventListener('pointerdown', function (e) { if (panelEl && !panelEl.hidden && !root.contains(e.target)) setOpen(false); });
  setInterval(function () { if (!document.hidden) safeRun('tick', updateTimes); }, 60000);

  /* ── boot ── */
  Promise.resolve(window.LF_VIDEOS_READY).catch(function () {}).then(function () {
    safeRun('boot', function () {
      var now = Date.now();
      var videos = (window.LF_CONFIG && window.LF_CONFIG.videos) || [];
      entries = normalize(fromVideos(videos, now), now);
      if (entries.length) build();        /* nothing to show -> stay hidden */
    });
  });

  /* test/debug hook — lets the pure pieces be exercised without a fetch */
  window.LFJ_STATUS_FEED = { normalize: normalize, fromVideos: fromVideos, timeFromKey: timeFromKey, ago: ago };
}());
